// ─────────────────────────────────────────────────────────────────
//  PatientVisitHistory.jsx  —  v3  "Clinical Clarity" enhanced
//  • Header with count + PRN chips
//  • Rows use v3 .pd-row / .pd-date-tile (accent bar + gradient tile on hover)
//  • Vitals grid with accent top bars; prescription cards refined
//  • Modal banner on GRAD_HERO with brand-tinted tabs
//  Props & exports unchanged.
// ─────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import {
  Modal, Button, Flex, Typography,
  Tabs, Tag, Spin, Alert, Badge, Empty,
} from "antd";
import {
  FileTextOutlined, CalendarOutlined,
  HeartOutlined, MedicineBoxOutlined,
  ExperimentOutlined, ClockCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  G700, G100, G50, GRAY4, GRAY6, GRAY7,
  SLATE, SLATE2, MUTED, WHITE,
  GRAD_HERO, GRAD_BRAND,
  RADIUS_SM, RADIUS_MD, RADIUS_LG, RADIUS_PILL,
  SHADOW_BLUE,
  FF_DISPLAY, FF_BODY, FF_MONO,
} from "./patientTokens";
import { StatusBadge } from "./PatientShared";
import { useVisitSummary } from "./Usevisitsummary";

import {
  formatVisitDate,
  formatVisitTime,
  fromNowVisit,
  visitDateParts,
} from "./dateUtils";

dayjs.extend(relativeTime);
const { Title, Text } = Typography;

// ── InfoRow ────────────────────────────────────────────────────
function InfoRow({ label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: "flex", gap: 10, padding: "7px 0",
      borderBottom: `1px solid ${GRAY7}`,
    }}>
      <Text style={{
        color: MUTED, fontSize: 12, fontWeight: 500,
        minWidth: 140, flexShrink: 0, fontFamily: FF_BODY,
      }}>
        {label}
      </Text>
      <Text style={{ color: SLATE, fontSize: 13, fontFamily: mono ? FF_MONO : FF_BODY, flex: 1 }}>
        {value}
      </Text>
    </div>
  );
}

// ── Section ────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div style={{
      background: WHITE, border: `1px solid ${GRAY6}`,
      borderRadius: RADIUS_MD, padding: "13px 15px", marginBottom: 12,
    }}>
      <Flex align="center" gap={9} style={{ marginBottom: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
          display: "flex", alignItems: "center",
          justifyContent: "center", color: WHITE, fontSize: 14, flexShrink: 0,
        }}>
          {icon}
        </div>
        <Text strong style={{ color: SLATE, fontSize: 13, fontFamily: FF_DISPLAY, letterSpacing: "-0.2px" }}>
          {title}
        </Text>
      </Flex>
      {children}
    </div>
  );
}

// ── Vitals tab ─────────────────────────────────────────────────
export function VitalsTab({ vitals }) {
  if (!vitals) return <Empty description="No vital signs recorded" style={{ padding: "32px 0" }} />;
  const v = vitals;

  const VitalChip = ({ label, value, unit, highlight }) => (
    <div style={{
      position: "relative",
      background: highlight ? G50 : "#fafcff",
      border: `1px solid ${highlight ? G100 : GRAY6}`,
      borderRadius: RADIUS_SM, padding: "12px 8px 10px",
      textAlign: "center", overflow: "hidden",
    }}>
      {highlight && (
        <span style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: GRAD_BRAND, borderRadius: "3px 3px 0 0",
        }} />
      )}
      <Text style={{
        color: MUTED, fontSize: 9.5, display: "block", marginBottom: 3,
        fontFamily: FF_BODY, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}>
        {label}
      </Text>
      <Text strong style={{
        color: highlight ? G700 : SLATE, fontSize: 19, display: "block",
        lineHeight: 1, fontFamily: FF_DISPLAY, fontVariantNumeric: "tabular-nums",
      }}>
        {value ?? "—"}
      </Text>
      {unit && <Text style={{ color: MUTED, fontSize: 9, fontFamily: FF_MONO }}>{unit}</Text>}
    </div>
  );

  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))",
        gap: 8, marginBottom: 14,
      }}>
        <VitalChip label="Pulse"      value={v.pulse}      unit="bpm"          highlight />
        <VitalChip label="Systolic"   value={v.systolic}   unit="mmHg"         highlight />
        <VitalChip label="Diastolic"  value={v.diastolic}  unit="mmHg"         highlight />
        <VitalChip label="Temp"       value={v.temprature} unit="°C" />
        <VitalChip label="Resp"       value={v.resp}       unit="br/min" />
        <VitalChip label="SpO₂"       value={v.oxy_sat}    unit="%" />
        <VitalChip label="Weight"     value={v.weight}     unit="kg" />
        <VitalChip label="Height"     value={v.height}     unit="cm" />
        <VitalChip label="BMI"        value={v.bmi}        unit={v.bmi_status} highlight />
        <VitalChip label="Abd. Girth" value={v.abd_girth}  unit="cm" />
      </div>
      <Section icon={<HeartOutlined />} title="Nurse Notes & Triage">
        <InfoRow label="Triage Status" value={v.traige_status} />
        <InfoRow label="Nurse's Note"  value={v.nurse_note} />
        <InfoRow label="Recorded At"   value={v.vdate ? dayjs(v.vdate).format("MMM D YYYY, h:mm A") : null} />
      </Section>
    </div>
  );
}

// ── Prescriptions tab ──────────────────────────────────────────
export function PrescriptionsTab({ prescriptions }) {
  if (!prescriptions?.length) return <Empty description="No prescriptions" style={{ padding: "32px 0" }} />;
  const statusColor = (s) => ({
    dispensed: "green", cancelled: "red", pending: "orange", active: "blue",
  })[s?.toLowerCase()] || "default";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {prescriptions.map((rx, i) => (
        <div key={rx.id || i} style={{
          background: WHITE, border: `1px solid ${GRAY6}`,
          borderRadius: RADIUS_MD, padding: "13px 15px",
          borderLeft: "none",
          position: "relative", overflow: "hidden",
        }}>
          {/* Gradient accent edge */}
          <span style={{
            position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
            background: GRAD_BRAND,
          }} />
          <Flex justify="space-between" align="flex-start" style={{ marginBottom: 8 }}>
            <Flex align="center" gap={9}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: G50, border: `1px solid ${G100}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: G700, fontSize: 14, flexShrink: 0,
              }}>
                <MedicineBoxOutlined />
              </div>
              <div>
                <Text strong style={{ color: SLATE, fontSize: 13, fontFamily: FF_BODY }}>
                  {rx.drug_name || `Drug #${rx.drug_id}`}
                </Text>
                <Text style={{ color: MUTED, fontSize: 11, display: "block", fontFamily: FF_BODY }}>
                  {[rx.frequency_label, rx.drug_route].filter(Boolean).join(" · ")}
                </Text>
              </div>
            </Flex>
            {rx.status && (
              <Tag color={statusColor(rx.status)} style={{ borderRadius: RADIUS_PILL, fontSize: 11 }}>
                {rx.status}
              </Tag>
            )}
          </Flex>
          <div style={{ paddingLeft: 41 }}>
            <InfoRow label="Dosage"         value={rx.dosage} />
            <InfoRow label="Qty Prescribed" value={rx.qty} />
            <InfoRow label="Qty Dispensed"  value={rx.qty_dis} />
            <InfoRow label="Duration"       value={rx.duration ? `${rx.duration} day(s)` : null} />
            <InfoRow label="Instructions"   value={rx.doctor_instruction} />
            <InfoRow label="Remark"         value={rx.paremark} />
            {rx.dispensed_date && <InfoRow label="Dispensed On" value={dayjs(rx.dispensed_date).format("MMM D YYYY, h:mm A")} />}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Visit list row (v3 pd-row / pd-date-tile) ──────────────────
function VisitListItem({ visit, onOpen }) {
  const { dayNum, month } = visitDateParts(visit.visited_no);
  const fullDate = formatVisitDate(visit.visited_no);
  const hasDoctor    = visit.doctor_name || visit.speciality;
  const hasDiagnosis = visit.diagnosis   || visit.medical_status;

  return (
    <div
      className="pd-row clickable"
      onClick={() => onOpen(visit)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(visit)}
      style={{ minHeight: 76, padding: "14px 18px" }}
    >
      {/* Date tile */}
      <div className="pd-date-tile" style={{ width: 54, height: 54, borderRadius: RADIUS_MD }}>
        <span className="pd-dt-day" style={{
          fontSize: 18, fontWeight: 700, color: G700, lineHeight: 1, fontFamily: FF_DISPLAY,
        }}>
          {dayNum}
        </span>
        <span className="pd-dt-mon" style={{
          fontSize: 8.5, fontWeight: 700, color: MUTED, marginTop: 2,
          textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: FF_BODY,
        }}>
          {month}
        </span>
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{
          fontSize: 15, fontWeight: 600, color: SLATE, display: "block",
          fontFamily: FF_DISPLAY, letterSpacing: "-0.3px", marginBottom: 4,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fullDate}
        </Text>
        <Flex align="center" gap={8} wrap="wrap">
          <StatusBadge status={visit.status} />
          {visit.visit_number && (
            <Text style={{ color: MUTED, fontSize: 11, fontFamily: FF_MONO }}>
              {visit.visit_number}
            </Text>
          )}
          {hasDoctor && (
            <Text style={{ color: SLATE2, fontSize: 12, fontFamily: FF_BODY }}>
              {visit.doctor_name}{visit.speciality && ` · ${visit.speciality}`}
            </Text>
          )}
        </Flex>
        {hasDiagnosis && (
          <Text style={{
            color: MUTED, fontSize: 12, fontFamily: FF_BODY, display: "block", marginTop: 2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {visit.diagnosis || visit.medical_status}
          </Text>
        )}
      </div>

      {/* View pill */}
      <div style={{
        display: "flex", alignItems: "center", gap: 5,
        background: G50, padding: "5px 13px", borderRadius: RADIUS_PILL,
        border: `1px solid ${G100}`, flexShrink: 0,
      }}>
        <Text style={{ fontSize: 11, fontWeight: 600, color: G700, fontFamily: FF_BODY }}>View</Text>
        <ArrowRightOutlined className="pd-row-arrow" style={{ fontSize: 10, color: G700 }} />
      </div>
    </div>
  );
}

// ── Visit summary modal ─────────────────────────────────────────
export function VisitSummaryModal({ visit, token, open, onClose, dependentPrn, selfPrn }) {
  const { data, loading, error } = useVisitSummary(
    open ? visit?.visit_number : null,
    token,
    dependentPrn,
    selfPrn
  );

  const LoadingSlot = () => (
    <div style={{ padding: "20px 0" }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div className="pd-skeleton" style={{ width: 32, height: 32, borderRadius: RADIUS_SM }} />
          <div style={{ flex: 1 }}>
            <div className="pd-skeleton" style={{ width: "40%", height: 12, marginBottom: 8 }} />
            <div className="pd-skeleton" style={{ width: "80%", height: 10 }} />
          </div>
        </div>
      ))}
    </div>
  );

  const tabItems = [
    {
      key: "vitals",
      label: <Flex align="center" gap={5}><HeartOutlined /><span>Vitals</span></Flex>,
      children: loading ? <LoadingSlot /> : error ? <Alert type="error" message={error} /> : <VitalsTab vitals={data?.vitals} />,
    },
    {
      key: "prescriptions",
      label: (
        <Flex align="center" gap={5}>
          <MedicineBoxOutlined /><span>Rx</span>
          {data?.prescriptions?.length > 0 && (
            <Badge count={data.prescriptions.length} size="small" style={{ backgroundColor: G700 }} />
          )}
        </Flex>
      ),
      children: loading ? <LoadingSlot /> : error ? <Alert type="error" message={error} /> : <PrescriptionsTab prescriptions={data?.prescriptions} />,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose} style={{ borderRadius: RADIUS_SM, fontFamily: FF_BODY, fontWeight: 500, minHeight: 38 }}>
          Close
        </Button>
      }
      width="min(660px, 96vw)"
      style={{ top: 16 }}
      styles={{ body: { padding: 0, maxHeight: "82vh", overflowY: "auto" } }}
      title={
        <Flex align="center" gap={10}>
          <div style={{
            width: 34, height: 34, borderRadius: RADIUS_SM,
            background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
            display: "flex", alignItems: "center",
            justifyContent: "center", color: WHITE,
          }}>
            <FileTextOutlined />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, color: SLATE, display: "block", fontFamily: FF_DISPLAY, letterSpacing: "-0.2px" }}>
              Visit Details
            </Text>
            {visit?.visit_number && (
              <Text style={{ fontSize: 11, color: MUTED, fontFamily: FF_MONO }}>
                {visit.visit_number}
              </Text>
            )}
          </div>
        </Flex>
      }
    >
      {visit && (
        <>
          {/* Visit header banner — deep hero gradient */}
          <div style={{
            position: "relative",
            background: GRAD_HERO,
            padding: "14px 20px",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: "radial-gradient(300px 130px at 92% -30%, rgba(34,211,238,0.22), transparent 60%)",
            }} />
            <Flex align="center" gap={12} style={{ position: "relative" }}>
              <div style={{
                width: 44, height: 44, borderRadius: RADIUS_MD,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: WHITE, fontSize: 19, flexShrink: 0,
              }}>
                <CalendarOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ color: WHITE, display: "block", fontSize: 13.5, fontFamily: FF_BODY }}>
                  {formatVisitDate(visit.visited_no)}
                </Text>
                <Text style={{ color: "rgba(255,255,255,0.65)", fontSize: 11, fontFamily: FF_MONO }}>
                  {formatVisitTime(visit.visited_no)} · {fromNowVisit(visit.visited_no)}
                </Text>
              </div>
              <StatusBadge status={visit.status} />
            </Flex>
          </div>
          <div style={{ padding: "4px 16px 0" }}>
            <Tabs defaultActiveKey="vitals" items={tabItems} size="small" />
          </div>
        </>
      )}
    </Modal>
  );
}

// ── MAIN ──────────────────────────────────────────────────────
function PatientVisitHistory({ visits, patient, decoded, token, dependentPrn, selfPrn: selfPrnProp }) {
  const [modal, setModal] = useState({ open: false, visit: null });

  const selfPrn = selfPrnProp || decoded?.prn || "—";   // ← JWT prn is always the primary
  const viewingDependent = dependentPrn && dependentPrn !== selfPrn;
  const prn = viewingDependent ? dependentPrn : selfPrn;

  return (
    <div style={{ width: "100%" }}>
      {/* Header */}
      <div className="pd-up" style={{ marginBottom: 20 }}>
        <div className="pd-eyebrow"><FileTextOutlined /> Visit History</div>
        <Title level={3} style={{
          margin: "2px 0 6px", fontFamily: FF_DISPLAY, fontWeight: 800, color: SLATE,
          letterSpacing: "-0.6px", fontSize: "clamp(1.3rem, 3vw, 1.75rem)",
        }}>
          Your Hospital Visits
        </Title>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
            {visits.length} visit{visits.length !== 1 ? "s" : ""} on record
          </Text>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "2px 10px", borderRadius: RADIUS_PILL,
            background: G50, border: `1px solid ${G100}`,
            color: G700, fontSize: 11, fontWeight: 600, fontFamily: FF_MONO,
          }}>
            PRN {prn}
          </span>
          <Text style={{ color: MUTED, fontSize: 12, fontFamily: FF_BODY }}>
            · Tap any visit to view full details
          </Text>
        </div>
      </div>

      {/* List card */}
      <div className="pd-panel pd-up pd-up-1">
        {visits.length === 0 ? (
          <div style={{ textAlign: "center", padding: "56px 24px" }}>
            <div style={{
              width: 54, height: 54, borderRadius: RADIUS_MD, background: GRAY7,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 14px", fontSize: 24, color: GRAY4,
            }}>
              <FileTextOutlined />
            </div>
            <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 4, fontFamily: FF_DISPLAY }}>
              No visits on record
            </Text>
            <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
              Visit records will appear here after your first consultation.
            </Text>
          </div>
        ) : (
          visits.map((v, idx) => (
            <div key={v.id} style={{ animation: `pdUp 0.3s ease ${Math.min(idx * 0.04, 0.3)}s both` }}>
              <VisitListItem visit={v} onOpen={(visit) => setModal({ open: true, visit })} />
            </div>
          ))
        )}
      </div>

      <VisitSummaryModal
        visit={modal.visit}
        token={token}
        open={modal.open}
        onClose={() => setModal({ open: false, visit: null })}
        dependentPrn={viewingDependent ? dependentPrn : null}
        selfPrn={selfPrn}
      />
    </div>
  );
}

export default PatientVisitHistory;